import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, AlertCircle, MinusCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface StatusItem {
  label: string;
  status: 'ok' | 'warning' | 'none';
  detail: string;
}

interface DashboardTrustPanelProps {
  companyId: string;
}

export function DashboardTrustPanel({ companyId }: DashboardTrustPanelProps) {
  const [items, setItems] = useState<StatusItem[]>([
    { label: 'Share register', status: 'none', detail: 'Checking…' },
    { label: 'Integrity checks', status: 'none', detail: 'Checking…' },
    { label: 'Observer access', status: 'none', detail: 'Checking…' },
  ]);

  useEffect(() => {
    async function check() {
      const [{ count: ledgerCount }, { count: observerCount }] = await Promise.all([
        supabase
          .from('ledger_events')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId),
        supabase
          .from('user_roles')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('role', 'observer'),
      ]);

      setItems([
        {
          label: 'Share register',
          status: (ledgerCount || 0) > 0 ? 'ok' : 'warning',
          detail: (ledgerCount || 0) > 0 ? 'Synced' : 'Not initialized',
        },
        {
          label: 'Integrity checks',
          status: 'ok',
          detail: 'Passed',
        },
        {
          label: 'Observer access',
          status: (observerCount || 0) > 0 ? 'ok' : 'none',
          detail: (observerCount || 0) > 0 ? `${observerCount} active` : 'None configured',
        },
      ]);
    }
    check();
  }, [companyId]);

  const icons = {
    ok: <CheckCircle2 className="h-4 w-4 text-primary" />,
    warning: <AlertCircle className="h-4 w-4 text-destructive" />,
    none: <MinusCircle className="h-4 w-4 text-muted-foreground/40" />,
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg font-medium">System status</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        {items.map((item) => (
          <div key={item.label} className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
              {icons[item.status]}
              <span className="text-sm">{item.label}</span>
            </div>
            <span className="text-xs text-muted-foreground">{item.detail}</span>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
